const PENDING_INVITE_KEY = "wc.pendingInvite";

/**
 * OWNED BY C1. The invite link is `${APP_ORIGIN}/join#<token>` (F-801) — the token lives in the hash
 * fragment specifically because a fragment never reaches the server (S-014), so it can't leak into logs
 * or a Referer header. Nothing server-side or client-side ever consumed it: `POST /api/invites/accept`
 * exists and is tested, but no client code read the URL, so clicking an invite link silently did nothing.
 *
 * Captured into sessionStorage (not just read from `location` when needed) because the token has to
 * survive whatever the invitee does next: sign up, sign in, or a full-page Google OAuth redirect that
 * lands back on a different URL entirely. Call this once, as early and as unconditionally as possible
 * (App's render body, not an effect) so the URL is captured before anything can navigate away from it.
 */
export function capturePendingInvite(): void {
  if (location.pathname !== "/join" || location.hash.length <= 1) return;
  const token = location.hash.slice(1);
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    // Private browsing or storage disabled: the token can't survive a redirect, but the direct (no
    // OAuth detour) sign-in path still works since the URL itself is unchanged until this line runs.
  }
  history.replaceState(null, "", "/");
}

export function getPendingInvite(): string | null {
  try {
    return sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // Nothing to clean up if storage isn't available in the first place.
  }
}
